/**
 * BankReconciliationPage — does the bank agree with the books?
 *
 * The layout is the reconciliation itself, not a dashboard of it: the bank's
 * lines on the left, what the books say on the right, and the difference
 * stated at the top where it cannot be missed.
 *
 * The one behaviour worth defending: a line with two equally good explanations
 * is never ticked automatically. It is marked "needs review" and a person
 * picks. A wrong tick hides a real discrepancy, which is worse than no tick.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, Landmark, Wand2, CheckCircle2, AlertCircle, HelpCircle, Link2,
  Unlink, ArrowLeft, Upload, Scale, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import { todayIso } from '@/lib/localDate';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = '/api/finance/bank-reconciliation';

const call = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || 'Request failed');
  return body;
};

const money = (value, currency = 'USD') =>
  `${value < 0 ? '-' : ''}${currency} ${Math.abs(Number(value || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BANK_ACCOUNTS = [
  { code: '1101', label: 'Petty cash (1101)' },
  { code: '1102', label: 'Main bank account (1102)' },
  { code: '1103', label: 'Online payments (1103)' },
];

const STATUS = {
  matched: { icon: CheckCircle2, label: 'Matched', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  needs_review: { icon: HelpCircle, label: 'Needs review', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  unmatched: { icon: AlertCircle, label: 'Unmatched', className: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const MatchBadge = ({ line }) => {
  const style = STATUS[line.match_status] || STATUS.unmatched;
  const Icon = style.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${style.className}`}
           data-testid={`line-status-${line.match_status}`}>
      <Icon className="h-3 w-3" />
      {style.label}
      {line.match_status === 'matched' && line.matched_automatically === false && ' (by hand)'}
    </Badge>
  );
};

const ImportForm = ({ onDone }) => {
  const [accountCode, setAccountCode] = useState('1102');
  const [label, setLabel] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  const [opening, setOpening] = useState('');
  const [closing, setClosing] = useState('');
  const [csvText, setCsvText] = useState('');

  const importIt = useMutation({
    mutationFn: () => call(`${BASE}/statements`, {
      method: 'POST',
      body: JSON.stringify({
        account_code: accountCode, label,
        period_start: periodStart, period_end: periodEnd,
        opening_balance: Number(opening || 0),
        closing_balance: Number(closing || 0),
        csv_text: csvText,
      }),
    }),
    onSuccess: (data) => {
      toast.success(
        `${data.line_count} lines imported${data.duplicate_lines_skipped
          ? `, ${data.duplicate_lines_skipped} already on file` : ''}`,
      );
      setCsvText('');
      onDone(data);
    },
    onError: (e) => toast.error(e.message),
  });

  const ready = accountCode && periodStart && periodEnd && csvText.trim();

  return (
    <Card data-testid="statement-import-form">
      <CardHeader>
        <CardTitle className="text-lg">Import a statement</CardTitle>
        <CardDescription>
          Paste your bank&rsquo;s CSV export. Lines already on file are skipped, so
          re-uploading the same statement changes nothing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Account</Label>
            <Select value={accountCode} onValueChange={setAccountCode}>
              <SelectTrigger data-testid="import-account-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BANK_ACCOUNTS.map((a) => (
                  <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="stmt-label">What is this statement?</Label>
            <Input id="stmt-label" value={label} placeholder="e.g. March 2026 current account"
                   onChange={(e) => setLabel(e.target.value)} data-testid="import-label" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stmt-from">Period from</Label>
            <Input id="stmt-from" type="date" value={periodStart}
                   onChange={(e) => setPeriodStart(e.target.value)} data-testid="import-start" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stmt-to">Period to</Label>
            <Input id="stmt-to" type="date" value={periodEnd}
                   onChange={(e) => setPeriodEnd(e.target.value)} data-testid="import-end" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stmt-open">Opening balance</Label>
            <Input id="stmt-open" type="number" step="0.01" value={opening}
                   onChange={(e) => setOpening(e.target.value)} data-testid="import-opening" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stmt-close">Closing balance</Label>
            <Input id="stmt-close" type="number" step="0.01" value={closing}
                   onChange={(e) => setClosing(e.target.value)} data-testid="import-closing" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="stmt-csv">Statement lines (CSV)</Label>
          <Textarea
            id="stmt-csv" rows={6} value={csvText} className="font-mono text-xs"
            placeholder={'date,description,reference,amount\n2026-03-04,Cash banking,DEP-9912,1250.00\n2026-03-07,Bank charges,CHG-03,-12.50'}
            onChange={(e) => setCsvText(e.target.value)} data-testid="import-csv"
          />
          <p className="text-xs text-slate-500">
            Money in is positive, money out negative. Separate debit and credit
            columns work too.
          </p>
        </div>
        <Button disabled={!ready || importIt.isPending} onClick={() => importIt.mutate()}
                data-testid="import-submit">
          {importIt.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <Upload className="mr-2 h-4 w-4" />}
          Import statement
        </Button>
      </CardContent>
    </Card>
  );
};

const CandidatePicker = ({ line, onClose, onMatched }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['rec-candidates', line?.id || line?._id],
    queryFn: () => call(`${BASE}/lines/${line.id || line._id}/candidates`),
    enabled: !!line,
  });

  const choose = useMutation({
    mutationFn: (entryId) => call(`${BASE}/lines/${line.id || line._id}/match`, {
      method: 'POST', body: JSON.stringify({ entry_id: entryId }),
    }),
    onSuccess: () => { toast.success('Matched'); onMatched(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={!!line} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="candidate-picker">
        <DialogHeader>
          <DialogTitle>Which entry is this?</DialogTitle>
        </DialogHeader>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
          <span className="font-medium">{line?.date}</span> · {line?.description || 'no description'}
          {line?.reference && <span className="text-slate-500"> · ref {line.reference}</span>}
          <span className="ml-2 font-mono">{money(line?.amount, line?.currency)}</span>
        </div>
        {isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
        {data?.candidates?.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-500" data-testid="no-candidates">
            Nothing in the ledger matches this amount within the date window. It may
            be a bank charge, interest, or a receipt nobody has recorded yet.
          </p>
        )}
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {(data?.candidates || []).map((c) => (
            <div key={c.entry_id}
                 className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 transition-colors hover:border-indigo-300"
                 data-testid={`candidate-${c.entry_id}`}>
              <div className="min-w-0 text-sm">
                <p className="font-medium text-slate-800">{c.entry_number} · {c.date}</p>
                <p className="truncate text-xs text-slate-500">
                  {c.description}
                  {c.reference_number ? ` · ref ${c.reference_number}` : ''}
                  {c.source_type ? ` · from ${String(c.source_type).replace('_', ' ')}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {c.same_reference && <Badge variant="outline" className="text-[10px]">same reference</Badge>}
                {c.same_day && <Badge variant="outline" className="text-[10px]">same day</Badge>}
                <Button size="sm" onClick={() => choose.mutate(c.entry_id)}
                        data-testid={`choose-${c.entry_id}`}>
                  <Link2 className="mr-1 h-3.5 w-3.5" /> This one
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * iter 641 — lines nobody has come back to.
 *
 * Reconciliation names what does not match; it cannot notice that nobody
 * returned to it. An unmatched line is usually a timing difference that clears
 * within days. One still sitting there after a fortnight is an unrecorded
 * payment, a duplicate debit, or money that left and was never accounted for —
 * so the age is the signal, and it is shown before anybody has to ask.
 */
const StaleLines = ({ onOpen }) => {
  const [days, setDays] = useState(14);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['rec-alerts', days],
    queryFn: () => call(`${BASE}/alerts?days=${days}`),
    // Keep the previous answer on screen while a new threshold loads. Without
    // this the component unmounted on every keystroke and the day input lost
    // focus after the first digit — typing "300" left "3".
    placeholderData: (previous) => previous,
  });

  const chase = useMutation({
    mutationFn: () => call(`${BASE}/alerts/run?days=${days}&force=true`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.alerted) {
        toast.success(`${r.chased} line(s) sent to ${r.alerted} approver(s)`);
      } else if (r.skipped === 'email_not_sent') {
        // Queued is not sent. Saying "sent" when the mail is switched off is
        // the one thing this feature must not do.
        toast.warning('Email is switched off, so nothing was sent', {
          description: `${r.notified_in_app || 0} approver(s) got an in-app notice instead. `
            + 'These lines will be chased again on the next run.',
        });
      } else {
        toast.message('Nothing sent', {
          description: r.skipped === 'no_recipients'
            ? 'Nobody currently holds finance approval, so there is no one to tell.'
            : 'Nothing is overdue by this many days.',
        });
      }
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  if (error) {
    return (
      <Card className="mb-8 border-amber-300" data-testid="stale-lines-error">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            We couldn&apos;t check for unexplained lines
          </CardTitle>
          <CardDescription>{error.message}</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <Button size="sm" variant="outline" onClick={() => refetch()}
                  data-testid="stale-retry-btn">Try again</Button>
        </CardContent>
      </Card>
    );
  }
  if (isLoading || !data) return null;

  return (
    <Card className={`mb-8 ${data.count ? 'border-amber-300 bg-amber-50/60' : ''}`}
          data-testid="stale-lines-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className={`h-4 w-4 ${data.count ? 'text-amber-600' : 'text-slate-300'}`} />
              {data.count
                ? `${data.count} line${data.count === 1 ? '' : 's'} unexplained for over ${data.threshold_days} days`
                : `Nothing unexplained beyond ${data.threshold_days} days`}
            </CardTitle>
            <CardDescription>
              {data.count
                ? `Oldest is ${data.oldest_days} days old · ${money(data.unexplained_out)} out, ${money(data.unexplained_in)} in`
                : 'Every statement line has either been matched or is still within the window.'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Input type="number" min={1} max={365} value={days} className="w-20"
                   onChange={(e) => {
                     const next = Number(e.target.value);
                     // An empty box means "still typing", not "back to 14".
                     if (e.target.value === '') return;
                     if (Number.isFinite(next) && next >= 1) setDays(next);
                   }}
                   data-testid="stale-days-input" />
            <span className="text-xs text-slate-500">days</span>
            {data.count > 0 && (
              <Button size="sm" variant="outline" onClick={() => chase.mutate()}
                      disabled={chase.isPending} data-testid="stale-chase-btn">
                {chase.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Email the approvers'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      {data.count > 0 && (
        <CardContent className="space-y-1">
          {data.lines.slice(0, 8).map((line) => (
            <button key={line.line_id} onClick={() => onOpen(line.statement_id)}
                    className="flex w-full items-center justify-between gap-4 rounded-md border border-amber-200 bg-white p-3 text-left transition-colors hover:border-amber-400"
                    data-testid={`stale-line-${line.line_id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-800">
                  {line.description || 'No description on the statement'}
                </p>
                <p className="text-xs text-slate-500">
                  {line.date} · {line.age_days} days · {line.why}
                </p>
              </div>
              <span className={`shrink-0 font-mono text-sm ${line.amount < 0 ? 'text-rose-700' : 'text-slate-700'}`}>
                {money(line.amount)}
              </span>
            </button>
          ))}
          {data.lines.length > 8 && (
            <p className="pt-1 text-xs text-slate-500">and {data.lines.length - 8} more</p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default function BankReconciliationPage() {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState(null);
  const [picking, setPicking] = useState(null);

  const statements = useQuery({
    queryKey: ['rec-statements'],
    queryFn: () => call(`${BASE}/statements`),
  });

  const detail = useQuery({
    queryKey: ['rec-statement', openId],
    queryFn: () => call(`${BASE}/statements/${openId}`),
    enabled: !!openId,
  });

  const summary = useQuery({
    queryKey: ['rec-summary', openId],
    queryFn: () => call(`${BASE}/statements/${openId}/summary`),
    enabled: !!openId,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['rec-statement', openId] });
    queryClient.invalidateQueries({ queryKey: ['rec-summary', openId] });
    queryClient.invalidateQueries({ queryKey: ['rec-statements'] });
  };

  const autoMatch = useMutation({
    mutationFn: () => call(`${BASE}/statements/${openId}/auto-match`, { method: 'POST' }),
    onSuccess: (r) => {
      toast.success(
        `${r.matched} matched${r.needs_review ? `, ${r.needs_review} need a decision` : ''}`,
        { description: r.needs_review
          ? 'Lines with more than one possible entry were left for you — guessing would hide a real difference.'
          : undefined },
      );
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });

  const unmatch = useMutation({
    mutationFn: (lineId) => call(`${BASE}/lines/${lineId}/unmatch`, {
      method: 'POST', body: JSON.stringify({ reason: 'Unmatched from the reconciliation screen' }),
    }),
    onSuccess: () => { toast.success('Unmatched'); refresh(); },
    onError: (e) => toast.error(e.message),
  });

  const closeIt = useMutation({
    mutationFn: () => call(`${BASE}/statements/${openId}/close`, { method: 'POST' }),
    onSuccess: () => { toast.success('Statement reconciled'); refresh(); },
    onError: (e) => toast.error(e.message, {
      description: 'Everything on both sides has to be explained first.',
    }),
  });

  const rows = statements.data?.statements || [];
  const lines = detail.data?.lines || [];
  const s = summary.data;
  const alreadyClosed = detail.data?.statement?.status === 'reconciled';

  if (!openId) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="bank-reconciliation-page">
        <div className="mx-auto max-w-5xl px-6 py-10">
          <header className="mb-10 max-w-2xl">
            <a href="/admin/finance" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-slate-900" data-testid="back-to-finance">
              <ArrowLeft className="h-3 w-3" /> Finance
            </a>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-400">
              <Landmark className="h-3.5 w-3.5" /> Bank reconciliation
            </div>
            <h1 className="mt-2 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
              Does the bank agree with the books?
            </h1>
            <p className="mt-4 text-base text-slate-600">
              Import a statement and every line is matched against the ledger. Anything
              that cannot be explained on either side is named, not absorbed.
            </p>
          </header>

          <StaleLines onOpen={setOpenId} />

          <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
            <ImportForm onDone={(d) => { refresh(); setOpenId(d.id || d._id); }} />
            <Card data-testid="statement-list">
              <CardHeader>
                <CardTitle className="text-lg">Statements</CardTitle>
                <CardDescription>Most recent period first.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {statements.isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
                {!statements.isLoading && rows.length === 0 && (
                  <p className="text-sm text-slate-500" data-testid="statement-list-empty">
                    No statements imported yet.
                  </p>
                )}
                {rows.map((row) => (
                  <button
                    key={row.id || row._id}
                    onClick={() => setOpenId(row.id || row._id)}
                    className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors hover:border-indigo-300"
                    data-testid={`statement-${row.id || row._id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-800">{row.label}</p>
                      <p className="text-xs text-slate-500">
                        {row.period_start} to {row.period_end} · {row.line_count} lines
                      </p>
                    </div>
                    <Badge variant="outline"
                           className={row.status === 'reconciled'
                             ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                             : 'border-slate-200'}>
                      {row.status}
                    </Badge>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bank-reconciliation-detail">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <button onClick={() => setOpenId(null)}
                className="mb-6 inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-slate-900"
                data-testid="back-to-statements">
          <ArrowLeft className="h-3.5 w-3.5" /> All statements
        </button>

        {s && (
          <Card className={`mb-8 ${s.reconciled ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/30'}`}
                data-testid="reconciliation-summary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Scale className="h-4 w-4 text-slate-500" />
                {s.reconciled ? 'This statement reconciles' : 'Not yet reconciled'}
              </CardTitle>
              <CardDescription>
                {s.account_code} · {s.period.start} to {s.period.end}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  ['Opening balance', money(s.opening_balance, s.currency)],
                  ['Closing balance', money(s.closing_balance, s.currency)],
                  ['Matched movement', money(s.matched_movement, s.currency)],
                  ['Unexplained difference', money(s.unexplained_difference, s.currency)],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
                    <p className="mt-1 font-mono text-sm text-slate-900" data-testid={`summary-${label.split(' ')[0].toLowerCase()}`}>{value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                <span data-testid="summary-counts">
                  {s.lines.matched} matched · {s.lines.needs_review} need review ·{' '}
                  {s.lines.unmatched} unmatched
                </span>
                {s.in_books_not_on_statement.length > 0 && (
                  <span className="rounded-full bg-white px-2 py-1 text-amber-700"
                        data-testid="in-books-only">
                    {s.in_books_not_on_statement.length} ledger entries have no statement line
                    ({money(s.in_books_not_on_statement_total, s.currency)})
                  </span>
                )}
              </div>
              <div className="mt-5 flex gap-2">
                <Button size="sm" onClick={() => autoMatch.mutate()}
                        disabled={autoMatch.isPending} data-testid="auto-match-button">
                  {autoMatch.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
                  Match what is certain
                </Button>
                {/* iter 639 — found in review: this was disabled whenever the
                    summary said everything reconciled, which is the only state
                    in which closing succeeds. It is the *statement's* status
                    that says whether it has already been declared. */}
                <Button size="sm" variant="outline" onClick={() => closeIt.mutate()}
                        disabled={closeIt.isPending || alreadyClosed}
                        data-testid="close-statement-button">
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                  {alreadyClosed ? 'Reconciled' : 'Declare reconciled'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card data-testid="statement-lines">
          <CardHeader>
            <CardTitle className="text-lg">Statement lines</CardTitle>
            <CardDescription>
              What the bank says happened, and what the books say it was.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {detail.isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
            {lines.map((line) => (
              <div key={line.id || line._id}
                   className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3"
                   data-testid={`statement-line-${line.id || line._id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-slate-800">
                    <span className="font-mono text-xs text-slate-500">{line.date}</span>{' '}
                    {line.description || 'no description'}
                    {line.reference && <span className="text-xs text-slate-500"> · {line.reference}</span>}
                  </p>
                  {line.matched_entry_number && (
                    <p className="mt-0.5 text-xs text-emerald-700">
                      matched to {line.matched_entry_number}
                    </p>
                  )}
                  {line.match_status === 'needs_review' && (
                    <p className="mt-0.5 text-xs text-amber-700">
                      {line.candidate_count} possible entries — too close to call automatically
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono text-sm ${line.amount < 0 ? 'text-rose-600' : 'text-slate-900'}`}>
                    {money(line.amount, line.currency)}
                  </span>
                  <MatchBadge line={line} />
                  {line.match_status === 'matched' ? (
                    <Button size="sm" variant="ghost"
                            onClick={() => unmatch.mutate(line.id || line._id)}
                            data-testid={`unmatch-${line.id || line._id}`}>
                      <Unlink className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setPicking(line)}
                            data-testid={`find-match-${line.id || line._id}`}>
                      Find it
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {s?.in_books_not_on_statement?.length > 0 && (
          <Card className="mt-8" data-testid="books-not-on-statement">
            <CardHeader>
              <CardTitle className="text-lg">In the books, not on the statement</CardTitle>
              <CardDescription>
                Recorded in the ledger for this period with no corresponding bank line —
                uncleared cheques, or money that never reached the bank.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {s.in_books_not_on_statement.map((e) => (
                <div key={e.entry_id}
                     className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 text-sm"
                     data-testid={`unstated-${e.entry_id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">{e.entry_number} · {e.date}</p>
                    <p className="truncate text-xs text-slate-500">{e.description}</p>
                  </div>
                  <span className="font-mono">{money(e.amount, s.currency)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {picking && (
          <CandidatePicker line={picking} onClose={() => setPicking(null)} onMatched={refresh} />
        )}
      </div>
    </div>
  );
}
